import { FileImageOutlined } from '@ant-design/icons';
import { Button, Modal, Spin, Typography, message } from 'antd';
import { useEffect, useState } from 'react';
import { errorMessage } from '../../api/client';

interface Props {
  exists: boolean;
  title: string;
  fetchUrl: () => Promise<string>;
}

/**
 * Opens a transaction's proof image on demand — used for investment, profit payment and withdrawal
 * rows alike (each passes its own fetcher).
 *
 * Unlike the profile documents this is loaded lazily rather than inline: a table can hold dozens of
 * rows, and eagerly fetching an image for every one would be a lot of wasted traffic.
 */
export default function TransactionImageLink({ exists, title, fetchUrl }: Props) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url);
    },
    [url],
  );

  if (!exists) {
    return (
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        —
      </Typography.Text>
    );
  }

  const open = async () => {
    setLoading(true);
    try {
      setUrl(await fetchUrl());
    } catch (err) {
      message.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    if (url) URL.revokeObjectURL(url);
    setUrl(null);
  };

  return (
    <>
      <Button type="link" size="small" icon={<FileImageOutlined />} loading={loading} onClick={open}>
        View
      </Button>

      <Modal maskClosable={false} open={!!url} onCancel={close} footer={null} width={640} title={title} destroyOnClose>
        {url ? (
          <img src={url} alt="Transaction proof" style={{ width: '100%', borderRadius: 8 }} />
        ) : (
          <Spin />
        )}
      </Modal>
    </>
  );
}
